import { ReactComponent as UploadIcon } from 'assets/icons/icon-cloud-upload.svg';
import Button from 'components/Button';
import Page from 'components/Page';
import Table, { FixedColumnType } from 'components/Table/Table';
import { useAddBalancesMutation } from 'graphql/mutations/AddBalances.generated';
import { curr } from 'helpers/curr';
import React, { useState } from 'react';
import CSVReader from 'react-csv-reader';
import { toast } from 'react-toastify';
import {
  HeroIconArrowRight,
  useWrappedUrqlMutation,
  useFetchConversionRates,
  useModal,
} from 'toolbox';
import {
  useGetContextualDataForUploadQuery,
  GetContextualDataForUploadQuery,
} from './GetContextualDataForUpload.generated';
import './UploadBalance.css';
import Flex from 'toolbox/src/components/Flex/Flex';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import { routeMap, useNavigate } from 'routeMap';
import HeroIconExclamation from 'toolbox/src/components/HeroIcons/HeroIconExclamation';
import dayjs from 'dayjs';
import Stack from 'toolbox/src/components/Stack/Stack';
import Modal from 'toolbox/src/components/Modal/Modal';
import { UpdateBankAccountForm } from './UpdateBankAccountForm';
import { Product_Types_Enum_Enum } from 'graphql/schemaTypes.generated';
import { UseModalReturn } from 'toolbox/src/hooks/useModal';
import ResetAndSubmitButton from 'components/ResetAndSubmitButton/ResetAndSubmitButton';
import {
  useUpdateBankAccountFromBalanceScreenMutation,
  UpdateBankAccountFromBalanceScreenMutation,
} from './UpdateBankAccount.generated';

const isoDateRegex = /^([0-9]{4}).([0-9]{1,2}).([0-9]{1,2})/;
const gmtDateRegex = /^([0-9]{1,2}).([0-9]{1,2}).([0-9]{4})/;

const parseDate = (dateStr: string) => {
  if (dateStr && typeof dateStr === 'string') {
    const matchISO = dateStr.match(isoDateRegex);
    const matchGMT = dateStr.match(gmtDateRegex);
    if (matchISO) return dateStr;
    else if (matchGMT.length === 4)
      return `${matchGMT[3]}-${matchGMT[2]}-${matchGMT[1]}`;
  }
  return null;
};

interface ImportedRow {
  bank_account_id: string;
  balance: number;
  balance_gbp: number;
  balance_eur: number;
  balance_usd: number;
  date: string;
  interest_rate: number;
  maturity_date: string;
}

type RowErrors =
  | ({ [K in keyof ImportedRowWithAccount]?: string } & { duplicate?: string })
  | undefined;

// Add these during development, but remember to remove them!
const devRows: ImportedRow[] = [];

export type ImportedRowWithAccount = ImportedRow & {
  account?: GetContextualDataForUploadQuery['bank_accounts'][0];
  errors: RowErrors;
};

const UploadBalance = () => {
  const [rows, setRows] = useState<ImportedRow[]>([...devRows]);
  const [error, setError] = useState('');

  const conversions = useFetchConversionRates(
    process.env.REACT_APP_EXCHANGERATE_API_KEY,
  );

  const addBalances = useWrappedUrqlMutation(useAddBalancesMutation);

  const bank_account_ids = rows.map((row) => {
    return row.bank_account_id?.toUpperCase() || '';
  });

  const [contextData] = useGetContextualDataForUploadQuery({
    variables: {
      bank_account_ids,
    },
    pause: !bank_account_ids || bank_account_ids.length === 0,
  });

  const accounts = contextData.data?.bank_accounts || [];

  const rowsWithAccount: ImportedRowWithAccount[] = rows.map((row) => {
    const rowWithAccount = {
      ...row,
      account: accounts.find(
        (account) => account.account_id === row.bank_account_id.toUpperCase(),
      ),
    };

    const rowWithCurrencies = {
      ...rowWithAccount,
      balance_gbp: rowWithAccount?.balance_gbp
        ? rowWithAccount.balance_gbp // use balance_gbp from import file if present
        : rowWithAccount.account?.currency?.prefix === 'GBP'
        ? rowWithAccount.balance // no need to convert if it's already GBP
        : conversions.convert({
            base: rowWithAccount?.account?.currency?.prefix as any,
            amount: rowWithAccount?.balance,
            target: 'GBP',
          }),
      balance_eur: rowWithAccount?.balance_eur
        ? rowWithAccount.balance_eur // use balance_eur from import file if present
        : rowWithAccount.account?.currency?.prefix === 'EUR'
        ? rowWithAccount.balance // no need to convert if it's already EUR
        : conversions.convert({
            base: rowWithAccount?.account?.currency?.prefix as any,
            amount: rowWithAccount?.balance,
            target: 'EUR',
          }),
      balance_usd: rowWithAccount?.balance_usd
        ? rowWithAccount.balance_usd // use balance_usd from import file if present
        : rowWithAccount.account?.currency?.prefix === 'USD'
        ? rowWithAccount.balance // no need to convert if it's already USD
        : conversions.convert({
            base: rowWithAccount?.account?.currency?.prefix as any,
            amount: rowWithAccount?.balance,
            target: 'USD',
          }),
    };

    return {
      ...rowWithCurrencies,
      errors: validateRow(rowWithCurrencies, rows),
    };
  });

  const tableColumns: FixedColumnType<ImportedRowWithAccount>[] = [
    {
      Header: 'Account',
      accessor: ({
        account,
        errors,
        bank_account_id,
        interest_rate,
        maturity_date,
        balance,
        balance_gbp,
        balance_eur,
        balance_usd,
        date,
      }) => {
        if (Object.values(errors || {}).length > 0) {
          return (
            <div
              onClick={() =>
                fixErrorsModal.onOpen({
                  balance,
                  balance_gbp,
                  bank_account_id,
                  interest_rate,
                  maturity_date,
                  date,
                  errors,
                  account,
                  balance_eur,
                  balance_usd,
                })
              }
              className="flex flex-col items-start p-1 text-red-600 bg-red-100 rounded-md cursor-pointer"
            >
              <div className="flex items-center flex-shrink-0 font-semibold">
                <HeroIconExclamation className="inline w-4 h-4 mb-px mr-1 text-red-600" />
                {account?.account_id.toUpperCase()}
              </div>
              {Object.values(errors).map((error, i) => (
                <div className="text-xs break-words opacity-75" key={i}>
                  {error}
                </div>
              ))}
            </div>
          );
        }

        const bankName = account?.bank?.name;
        const orgName =
          account?.currency_book?.business_unit?.organisation?.name;

        if (bankName) {
          return (
            <div className="flex flex-col">
              <div className="flex-shrink-0 text-sm font-semibold">
                {bank_account_id.toUpperCase()}
              </div>
              <div className="text-xs text-gray-600 truncate">{orgName}</div>
              <div className="text-xs text-gray-600 truncate">{bankName}</div>
            </div>
          );
        }
      },
    },
    {
      Header: 'Type',
      accessor: ({ account, interest_rate }) => {
        // if (errors.bank_account_id) return null;
        return (
          <div className="text-sm">
            {account?.product_type === 'NOTICE'
              ? account?.notice_days
                ? account?.notice_days + 'D NOTICE'
                : 'NOTICE_DAYS MISSING'
              : account?.product_type}
          </div>
        );
      },
    },
    {
      Header: 'Previous',
      accessor: ({ account, errors }) => {
        const previousBalance = account?.actual_balances?.[0];

        if (Object.values(errors || {}).length > 0 || !account) return null;

        return (
          <div className="flex flex-col">
            <div>
              {account?.currency?.prefix} {curr(previousBalance?.balance || 0)}
            </div>
            <div className="mt-1 text-xs text-gray-600">
              {account?.opening_interest_rate + '% AER'}
            </div>
            <div className="text-xs text-gray-600">
              AT {previousBalance?.date}
            </div>
          </div>
        );
      },
    },
    {
      id: 'gap',
      accessor: ({ errors }) =>
        Object.values(errors || {}).length > 0 ? null : (
          <HeroIconArrowRight className="w-3 mb-px text-gray-600" />
        ),
    },
    {
      Header: 'New',
      accessor: ({ balance, interest_rate, date, account, errors }) => {
        const baseCurrency = account?.currency.prefix;
        return (
          <div className="flex flex-col">
            <div>
              {baseCurrency} {curr(balance, account?.currency?.prefix)}
            </div>
            <div className="mt-1 text-xs text-gray-600 truncate">
              {interest_rate}% AER
            </div>
            <div className="text-xs text-gray-600 truncate">AT {date}</div>
          </div>
        );
      },
    },
    {
      Header: 'Currencies',
      accessor: ({
        balance,
        balance_gbp,
        balance_eur,
        balance_usd,
        errors,
        account,
      }) => {
        if (Object.values(errors || {}).length > 0) return null;
        const c = account?.currency?.prefix;
        return (
          <div className="text-xs">
            {c !== 'GBP' && (
              <p className="text-gray-600">
                {'GBP'} {curr(balance <= 0 ? 0 : balance_gbp)}
              </p>
            )}
            {c !== 'USD' && (
              <p className="text-gray-600">
                {'USD'} {curr(balance <= 0 ? 0 : balance_usd)}
              </p>
            )}
            {c !== 'EUR' && (
              <p className="text-gray-600">
                {'EUR'} {curr(balance <= 0 ? 0 : balance_eur)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Maturity Date',
      accessor: ({ account, date, maturity_date, errors }) => {
        if (Object.values(errors || {}).length > 0) return null;
        if (maturity_date && account?.product_type !== 'CALL') {
          return <span>{dayjs(maturity_date).format('YYYY-MM-DD')}</span>;
        } else if (account?.product_type === 'CALL') {
          return <p>{dayjs(new Date(date)).format('YYYY-MM-DD')}</p>;
        } else if (account?.product_type === 'NOTICE') {
          return (
            <p>
              {dayjs(new Date(date))
                .add(account?.notice_days, 'day')
                .format('YYYY-MM-DD')}
            </p>
          );
        } else if (account?.product_type === 'FIXED') {
          return (
            <p>{dayjs(account?.fixed_maturity_date).format('YYYY-MM-DD')}</p>
          );
        }
        return null;
      },
    },
  ];

  const updateBankAccount = useWrappedUrqlMutation(
    useUpdateBankAccountFromBalanceScreenMutation,
    {
      onSuccess: (result) => {
        // toast('Updated bank account with information from latest balance.');
      },
      onError: () => {},
    },
  );

  const resetButtonHandler = () => {
    setError('');
    setRows([]);
  };

  const navigate = useNavigate();

  const hasErrors = rowsWithAccount.some(
    (row) => Object.keys(row.errors || {}).length > 0,
  );

  const saveButtonHandler = () => {
    if (hasErrors) return;
    setError('');
    const objects = rowsWithAccount.map(
      ({
        bank_account_id,
        balance,
        balance_gbp,
        balance_eur,
        balance_usd,
        date,
        account,
        interest_rate,
        maturity_date,
      }) => {
        return {
          bank_account_id: bank_account_id.toUpperCase(),
          balance: Number(balance),
          balance_gbp: Number(balance_gbp),
          balance_usd: Number(balance_usd),
          balance_eur: Number(balance_eur),
          date,
          interest_rate: Number(
            (!isNaN(interest_rate) &&
              interest_rate !== undefined &&
              interest_rate !== null &&
              interest_rate.toString()) ||
              account?.opening_interest_rate.toString() ||
              '0',
          ),
          maturity_date:
            maturity_date || // if a maturity_date is given, use that
            (account?.product_type === 'CALL' && date) || // if the a/c type is CALL, use today
            (account?.product_type === 'NOTICE' && // if the a/c type is notice calculate the future date
              dayjs(new Date(date)).add(account?.notice_days, 'day')) ||
            (account?.product_type === 'FIXED' && account?.fixed_maturity_date),
        };
      },
    );

    rowsWithAccount
      .filter(
        (row) =>
          !!row.maturity_date ||
          (!isNaN(row.interest_rate) &&
            row.interest_rate !== undefined &&
            row.interest_rate !== null),
      )
      .map(({ account, maturity_date, interest_rate }) => {
        updateBankAccount.dispatch({
          account_id: account?.account_id,
          product_type: account?.product_type,
          notice_days: account?.notice_days,
          fixed_maturity_date: maturity_date || account.fixed_maturity_date,
          opening_interest_rate: Number(
            (!isNaN(interest_rate) &&
              interest_rate !== undefined &&
              interest_rate !== null &&
              interest_rate.toString()) ||
              account?.opening_interest_rate.toString() ||
              '0',
          ),
        });
      });

    addBalances
      .dispatch({ objects })
      .then((result) => {
        if (result?.error) {
          setError(result?.error.message);
          toast.error('Error whilst saving');
        } else toast.success('New Balances Saved');
        navigate.adminViewBalances();
      })
      .catch((err) => {
        setError(err);
        toast.error('Error whilst saving');
      });
  };

  const fixErrorsModal = useModal<ImportedRowWithAccount>();

  return (
    <Page
      icon={
        <Flex mx={1} alignItems="center">
          <div>
            <UploadIcon className="inline w-10 h-10 mr-2 -mb-1 text-gray-500" />
          </div>
          <Breadcrumbs
            links={[
              {
                label: 'View Balances',
                link: routeMap.adminViewBalances(),
              },
              {
                label: 'Upload Balances',
              },
            ]}
          />
        </Flex>
      }
    >
      {conversions.state === 'success' && (
        <div className="">
          {rows.length > 0 && !contextData.fetching && (
            <Stack my={2}>
              <Table<ImportedRowWithAccount>
                columns={tableColumns}
                data={rowsWithAccount}
              />
              <Flex justifyContent="flex-end" className="w-full">
                <Button
                  className="w-32 btn btn-white"
                  onClick={resetButtonHandler}
                >
                  Reset
                </Button>
                <Button className="w-32 btn" onClick={saveButtonHandler}>
                  Save
                </Button>
              </Flex>
            </Stack>
          )}
          {rows.length === 0 && (
            <label htmlFor="CSVFileInput" className="CSVReaderLabel">
              <CSVReader
                cssClass="CSVReader"
                cssInputClass="CSVReaderInput"
                inputId="CSVFileInput"
                label="Select a CSV with new balances"
                parserOptions={{
                  header: true,
                  dynamicTyping: false,
                  skipEmptyLines: true,
                }}
                onFileLoaded={(data) =>
                  setRows(
                    data.map((item) => ({
                      ...item,
                      balance: Number(item.balance),
                      balance_gbp: Number(item.balance_gbp),
                      balance_usd: Number(item.balance_usd),
                      balance_eur: Number(item.balance_eur),
                      interest_rate: Number(item.interest_rate),
                      date:
                        parseDate(item?.date) ||
                        dayjs(new Date()).format('YYYY-MM-DD'),
                      maturity_date: parseDate(item?.maturity_date) || null,
                      imported: false,
                    })),
                  )
                }
                onError={(error) =>
                  setError(error?.message || 'Error loading file')
                }
              />
            </label>
          )}
        </div>
      )}
      {error && (
        <pre className="max-w-5xl overflow-x-hidden text-sm font-semibold text-red-700 break-words">
          {JSON.stringify(error, null, 2)}
        </pre>
      )}
      <FixErrorsModal {...fixErrorsModal} />
    </Page>
  );
};

const FixErrorsModal: React.FC<UseModalReturn<ImportedRowWithAccount>> = (
  props,
) => {
  const shouldShowFixForm = Object.values(props.state?.errors || {}).some(
    (errorString: string) => {
      const errorsWhichShouldShowForm = [
        rowErrorStrings.noProductTypeProvidedOnAccount,
        rowErrorStrings.noNoticeDaysOnAccount,
        rowErrorStrings.noFixedMaturityDateOnAccount,
        rowErrorStrings.invalidProductTypeOnAccount,
      ];
      return errorsWhichShouldShowForm.includes(errorString);
    },
  );
  return (
    <Modal {...props}>
      <div className="p-6 bg-gray-100">
        <Stack className="max-w-sm">
          <h2 className="text-lg font-semibold text-gray-700">
            Errors Found On Import
          </h2>
          {Object.entries(props.state?.errors || {}).map(
            ([key, errorString], i) => {
              return (
                // TODO - handle different errors here based on strings passed
                <p className="text-base font-semibold text-red-700" key={i}>
                  {errorString}
                </p>
              );
            },
          )}
          {shouldShowFixForm && (
            <FixBankAccount
              onSuccess={(result) => {
                const newErrors = validateRow({
                  ...props.state,
                  account: result.update_bank_accounts_by_pk,
                });
                if (!newErrors) {
                  props.onClose();
                } else {
                  props.setState({ ...props.state, errors: newErrors });
                }
              }}
              {...props.state.account}
            />
          )}
          {!shouldShowFixForm && (
            <Flex justifyContent="flex-end">
              <Button className="btn btn-white" onClick={props.onClose}>
                Close
              </Button>
            </Flex>
          )}
        </Stack>
      </div>
    </Modal>
  );
};

const FixBankAccount = ({
  account_id,
  product_type,
  account_description,
  fixed_maturity_date,
  opening_interest_rate,
  notice_days,
  onSuccess,
}: {
  account_id: string;
  notice_days?: number;
  product_type: Product_Types_Enum_Enum;
  account_description?: string;
  fixed_maturity_date?: string;
  opening_interest_rate?: string;
  onSuccess: (mutation: UpdateBankAccountFromBalanceScreenMutation) => void;
}) => {
  const update = useWrappedUrqlMutation(
    useUpdateBankAccountFromBalanceScreenMutation,
    {
      onSuccess: (result) => {
        toast('Updated bank account successfully.');
        onSuccess(result);
      },
      onError: (error) => {
        toast.error('Could not update bank account');
        console.warn(error);
      },
    },
  );
  return (
    <UpdateBankAccountForm.Wrapper
      onSubmit={update.dispatch}
      initialValues={{
        account_id: account_id,
        notice_days,
        product_type,
        account_description,
        fixed_maturity_date,
        opening_interest_rate: opening_interest_rate || 0,
      }}
    >
      <UpdateBankAccountForm.Inputs.account_description label="Description" />
      <UpdateBankAccountForm.Inputs.product_type label="Product Type" />
      <UpdateBankAccountForm.Consumer>
        {({ values }) => {
          if (values.product_type === 'FIXED') {
            return (
              <UpdateBankAccountForm.Inputs.fixed_maturity_date label="Maturity Date" />
            );
          }
          if (values.product_type === 'NOTICE') {
            return (
              <UpdateBankAccountForm.Inputs.notice_days label="Notice Days" />
            );
          }
          return null;
        }}
      </UpdateBankAccountForm.Consumer>
      <UpdateBankAccountForm.Inputs.opening_interest_rate label="Interest Rate" />
      <ResetAndSubmitButton />
    </UpdateBankAccountForm.Wrapper>
  );
};

export default UploadBalance;

export const validateRow = (
  row: Partial<ImportedRowWithAccount>,
  otherRows?: Partial<ImportedRowWithAccount>[],
): RowErrors => {
  if (
    otherRows &&
    otherRows.filter((r) => r.bank_account_id === row.bank_account_id).length >
      1
  ) {
    return {
      duplicate: rowErrorStrings.duplicateImport,
    };
  }

  if (!row.bank_account_id) {
    return { bank_account_id: rowErrorStrings.noAccountIdProvided };
  }

  if (!row.account) {
    return {
      bank_account_id: rowErrorStrings.noAccountFoundForId(row.bank_account_id),
    };
  }

  if (!row.account?.product_type) {
    return {
      bank_account_id: rowErrorStrings.noProductTypeProvidedOnAccount,
    };
  }

  if (!validProductTypes.includes(row.account.product_type)) {
    return {
      bank_account_id: rowErrorStrings.invalidProductTypeOnAccount,
    };
  }

  if (
    row.account.product_type === 'FIXED' &&
    !row.account.fixed_maturity_date
  ) {
    return {
      bank_account_id: rowErrorStrings.noFixedMaturityDateOnAccount,
    };
  }

  if (row.account.product_type === 'NOTICE' && !row.account.notice_days) {
    return {
      bank_account_id: rowErrorStrings.noNoticeDaysOnAccount,
    };
  }

  if (typeof row.balance !== 'number' || isNaN(row.balance)) {
    return {
      balance: rowErrorStrings.noBalanceProvided,
    };
  }
  if (typeof row.balance_gbp !== 'number' || isNaN(row.balance_gbp)) {
    return {
      balance_gbp: rowErrorStrings.noConversionRate,
    };
  }

  if (!row.date) {
    return {
      date: rowErrorStrings.noDateProvided,
    };
  }
};

const validProductTypes = ['CALL', 'FIXED', 'NOTICE'];

export const rowErrorStrings = {
  noAccountFoundForId: (id: string) =>
    `No bank accounts were found the ID given: ${id}. Provide a bank account ID which links to an account in our database.`,
  duplicateImport:
    'You have more than one balance for the same bank account in this batch',
  noAccountIdProvided: 'You must provide a bank account ID with this row.',
  noBalanceProvided: 'You must provide a balance with this row.',
  noConversionRate:
    'We cannot get an exchange rate for this balance, you must add a GBP balance manually.',
  noDateProvided: 'You must provide a date on this row.',
  noInterestRate: 'There is no interest rate specified',
  noProductTypeProvidedOnAccount:
    'You must provide a product type on this bank account',
  invalidProductTypeOnAccount:
    'The product type on this bank account is not either CALL, FIXED or NOTICE.',
  noFixedMaturityDateOnAccount:
    'You must provide a fixed maturity date on FIXED accounts.',
  noNoticeDaysOnAccount:
    'You must provide the amount of notice days given on a NOTICE account.',
};
